import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Send,
  Users,
  UserCircle,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Plus,
  History,
  Loader2,
  PartyPopper,
  HandHeart,
  Megaphone,
  Wallet,
  Trophy,
  MessageSquare,
  Sparkles,
  Wand2,
  Eye
} from "lucide-react";

// Email template options
const emailTemplates = [
  { value: "campaign", label: "General Message", icon: MessageSquare, description: "Standard email with your custom content" },
  { value: "greeting", label: "Greetings & Wishes", icon: PartyPopper, description: "Festival wishes, New Year, special occasions" },
  { value: "welcome", label: "Welcome Note", icon: HandHeart, description: "Welcome new clients to TAT Atelier" },
  { value: "announcement", label: "News / Announcement", icon: Megaphone, description: "Company updates, achievements, news" },
  { value: "salary_disbursed", label: "Salary Disbursed", icon: Wallet, description: "Thank staff for salary credit notification" },
  { value: "milestone", label: "Milestone Celebration", icon: Trophy, description: "Celebrate project milestones with clients" }
];

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [recipients, setRecipients] = useState({ clients: [], team: [] });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("compose");
  
  // Form state
  const [emailTemplate, setEmailTemplate] = useState("campaign");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientType, setRecipientType] = useState("all");
  
  // Template-specific fields
  const [templateData, setTemplateData] = useState({
    occasion: "",
    emoji: "✨",
    title: "",
    month: "",
    amount: "",
    project_name: "",
    milestone: ""
  });

  // AI Update state
  const [aiUpdate, setAiUpdate] = useState({
    shortUpdate: "",
    selectedClients: [],
    projectId: "",
    tone: "warm_professional"
  });
  const [aiPreview, setAiPreview] = useState(null);
  const [aiPreviewing, setAiPreviewing] = useState(false);
  const [aiSending, setAiSending] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, recipientsRes, projectsRes] = await Promise.all([
        apiClient.get("/campaigns"),
        apiClient.get("/campaigns/recipients/suggestions"),
        apiClient.get("/projects")
      ]);
      setCampaigns(campaignsRes.data);
      setRecipients(recipientsRes.data);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load campaign data");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRecipient = (recipient) => {
    const exists = selectedRecipients.find(r => r.email === recipient.email);
    if (exists) {
      setSelectedRecipients(selectedRecipients.filter(r => r.email !== recipient.email));
    } else {
      setSelectedRecipients([...selectedRecipients, recipient]);
    }
  };

  const handleSelectAll = (type) => {
    const list = type === "clients" ? recipients.clients : recipients.team;
    const allSelected = list.every(r => selectedRecipients.find(s => s.email === r.email));
    
    if (allSelected) {
      setSelectedRecipients(selectedRecipients.filter(r => r.type !== type.slice(0, -1)));
    } else {
      const newRecipients = list.filter(r => !selectedRecipients.find(s => s.email === r.email));
      setSelectedRecipients([...selectedRecipients, ...newRecipients]);
    }
  };

  const handleSendCampaign = async () => {
    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    if (selectedRecipients.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }

    // Validate template-specific fields
    if (emailTemplate === "greeting" && !templateData.occasion) {
      toast.error("Please enter the occasion name");
      return;
    }
    if (emailTemplate === "salary_disbursed" && (!templateData.month || !templateData.amount)) {
      toast.error("Please enter month and amount for salary notification");
      return;
    }

    setSending(true);
    try {
      const response = await apiClient.post("/campaigns/send", {
        subject,
        message,
        template_type: emailTemplate,
        template_data: templateData,
        recipients: selectedRecipients.map(r => ({
          email: r.email,
          name: r.name,
          type: r.type
        })),
        send_immediately: true
      });

      if (response.data.failed_count > 0) {
        toast.warning(`Campaign sent with ${response.data.failed_count} failures`);
      } else {
        toast.success(`Campaign sent to ${response.data.sent_count} recipients`);
      }

      // Reset form
      setEmailTemplate("campaign");
      setSubject("");
      setMessage("");
      setSelectedRecipients([]);
      setTemplateData({ occasion: "", emoji: "✨", title: "", month: "", amount: "", project_name: "", milestone: "" });
      setActiveTab("history");
      fetchData();
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast.error(error.response?.data?.detail || "Failed to send campaign");
    } finally {
      setSending(false);
    }
  };

  // AI Update handlers
  const handleAiPreview = async () => {
    if (!aiUpdate.shortUpdate.trim()) {
      toast.error("Please enter an update message");
      return;
    }
    if (aiUpdate.selectedClients.length === 0) {
      toast.error("Please select at least one client");
      return;
    }
    
    setAiPreviewing(true);
    try {
      const response = await apiClient.post("/campaigns/ai-update/preview", {
        short_update: aiUpdate.shortUpdate,
        client_ids: aiUpdate.selectedClients,
        project_id: aiUpdate.projectId || null,
        tone: aiUpdate.tone
      });
      setAiPreview(response.data);
      toast.success("AI preview generated!");
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error(error.response?.data?.detail || "Failed to generate AI preview");
    } finally {
      setAiPreviewing(false);
    }
  };

  const handleAiSend = async () => {
    if (!aiPreview) {
      toast.error("Please preview the message first");
      return;
    }
    
    setAiSending(true);
    try {
      const response = await apiClient.post("/campaigns/ai-update/send", {
        short_update: aiUpdate.shortUpdate,
        client_ids: aiUpdate.selectedClients,
        project_id: aiUpdate.projectId || null,
        tone: aiUpdate.tone
      });
      
      toast.success(`Update sent to ${response.data.sent_count} client(s)!`);
      
      // Reset
      setAiUpdate({ shortUpdate: "", selectedClients: [], projectId: "", tone: "warm_professional" });
      setAiPreview(null);
      setActiveTab("history");
      fetchData();
    } catch (error) {
      console.error("Error sending update:", error);
      toast.error(error.response?.data?.detail || "Failed to send update");
    } finally {
      setAiSending(false);
    }
  };

  const toggleAiClient = (clientId) => {
    setAiUpdate(prev => ({
      ...prev,
      selectedClients: prev.selectedClients.includes(clientId)
        ? prev.selectedClients.filter(id => id !== clientId)
        : [...prev.selectedClients, clientId]
    }));
    // Clear preview when clients change
    if (aiPreview) setAiPreview(null);
  };

  const filteredClients = recipients.clients.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTeam = recipients.team.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      completed: "bg-green-500/20 text-green-400",
      completed_with_errors: "bg-yellow-500/20 text-yellow-400",
      sending: "bg-blue-500/20 text-blue-400",
      draft: "bg-gray-500/20 text-gray-400",
      failed: "bg-red-500/20 text-red-400"
    };
    const icons = {
      completed: <CheckCircle2 className="w-3 h-3" />,
      completed_with_errors: <AlertCircle className="w-3 h-3" />,
      sending: <Loader2 className="w-3 h-3 animate-spin" />,
      draft: <Clock className="w-3 h-3" />,
      failed: <XCircle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {icons[status]}
        {status?.replace("_", " ")}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="campaigns-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Email Campaigns</h1>
          <p className="text-gray-500">Send custom emails to clients and team members</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setActiveTab("compose")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === "compose" 
              ? "bg-orange-500 text-white" 
              : "bg-[#1A1A1A] text-gray-400 hover:text-white"
          }`}
          data-testid="tab-compose"
        >
          <Send className="w-4 h-4" />
          Compose
        </button>
        <button
          onClick={() => setActiveTab("ai-update")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === "ai-update" 
              ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white" 
              : "bg-[#1A1A1A] text-gray-400 hover:text-white"
          }`}
          data-testid="tab-ai-update"
        >
          <Sparkles className="w-4 h-4" />
          AI Updates
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === "history" 
              ? "bg-orange-500 text-white" 
              : "bg-[#1A1A1A] text-gray-400 hover:text-white"
          }`}
          data-testid="tab-history"
        >
          <History className="w-4 h-4" />
          History
          {campaigns.length > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
              {campaigns.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === "compose" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compose Form */}
          <div className="card" data-testid="compose-form">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-500" />
              Compose Email
            </h2>

            <div className="space-y-4">
              {/* Template Selector */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Email Template</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {emailTemplates.map((template) => {
                    const Icon = template.icon;
                    const isSelected = emailTemplate === template.value;
                    return (
                      <button
                        key={template.value}
                        onClick={() => setEmailTemplate(template.value)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          isSelected 
                            ? "border-orange-500 bg-orange-500/10" 
                            : "border-[#262626] hover:border-[#404040]"
                        }`}
                      >
                        <Icon className={`w-4 h-4 mb-1 ${isSelected ? "text-orange-500" : "text-gray-500"}`} />
                        <p className={`text-xs font-medium ${isSelected ? "text-white" : "text-gray-400"}`}>
                          {template.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {emailTemplates.find(t => t.value === emailTemplate)?.description}
                </p>
              </div>

              {/* Template-specific fields */}
              {emailTemplate === "greeting" && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-[#171717] rounded-lg border border-[#262626]">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Occasion *</label>
                    <Input
                      value={templateData.occasion}
                      onChange={(e) => setTemplateData({...templateData, occasion: e.target.value})}
                      placeholder="e.g., Happy Diwali"
                      className="bg-[#1A1A1A] border-[#262626] text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Emoji</label>
                    <Input
                      value={templateData.emoji}
                      onChange={(e) => setTemplateData({...templateData, emoji: e.target.value})}
                      placeholder="✨"
                      className="bg-[#1A1A1A] border-[#262626] text-white text-sm"
                    />
                  </div>
                </div>
              )}

              {emailTemplate === "announcement" && (
                <div className="p-3 bg-[#171717] rounded-lg border border-[#262626]">
                  <label className="block text-xs text-gray-500 mb-1">Announcement Title</label>
                  <Input
                    value={templateData.title}
                    onChange={(e) => setTemplateData({...templateData, title: e.target.value})}
                    placeholder="e.g., Exciting News!"
                    className="bg-[#1A1A1A] border-[#262626] text-white text-sm"
                  />
                </div>
              )}

              {emailTemplate === "salary_disbursed" && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-[#171717] rounded-lg border border-[#262626]">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Month *</label>
                    <Input
                      value={templateData.month}
                      onChange={(e) => setTemplateData({...templateData, month: e.target.value})}
                      placeholder="e.g., January 2026"
                      className="bg-[#1A1A1A] border-[#262626] text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Amount *</label>
                    <Input
                      value={templateData.amount}
                      onChange={(e) => setTemplateData({...templateData, amount: e.target.value})}
                      placeholder="e.g., 25,000"
                      className="bg-[#1A1A1A] border-[#262626] text-white text-sm"
                    />
                  </div>
                </div>
              )}

              {emailTemplate === "milestone" && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-[#171717] rounded-lg border border-[#262626]">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Project Name</label>
                    <Input
                      value={templateData.project_name}
                      onChange={(e) => setTemplateData({...templateData, project_name: e.target.value})}
                      placeholder="Project name"
                      className="bg-[#1A1A1A] border-[#262626] text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Milestone</label>
                    <Input
                      value={templateData.milestone}
                      onChange={(e) => setTemplateData({...templateData, milestone: e.target.value})}
                      placeholder="e.g., Design Approved!"
                      className="bg-[#1A1A1A] border-[#262626] text-white text-sm"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-1">Subject *</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="bg-[#1A1A1A] border-[#262626] text-white"
                  data-testid="campaign-subject"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Message *</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your message here... (auto-formatted with paragraphs, bullet points, etc.)"
                  rows={8}
                  className="w-full bg-[#1A1A1A] border border-[#262626] rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  data-testid="campaign-message"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Auto-formats: Line breaks → paragraphs, &quot;- item&quot; → bullet lists, &quot;1. item&quot; → numbered lists
                </p>
              </div>

              <div className="pt-4 border-t border-[#262626]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">
                    Selected: <span className="text-orange-500 font-semibold">{selectedRecipients.length}</span> recipients
                  </span>
                </div>
                <Button
                  onClick={handleSendCampaign}
                  disabled={sending || selectedRecipients.length === 0}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  data-testid="send-campaign-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Campaign
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Recipients Selection */}
          <div className="card" data-testid="recipients-panel">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Select Recipients
            </h2>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search recipients..."
                className="pl-10 bg-[#1A1A1A] border-[#262626] text-white"
                data-testid="recipient-search"
              />
            </div>

            {/* Clients Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <UserCircle className="w-4 h-4" />
                  Clients ({filteredClients.length})
                </h3>
                <button
                  onClick={() => handleSelectAll("clients")}
                  className="text-xs text-orange-500 hover:text-orange-400"
                  data-testid="select-all-clients"
                >
                  {filteredClients.every(c => selectedRecipients.find(r => r.email === c.email)) 
                    ? "Deselect All" 
                    : "Select All"}
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                {filteredClients.length > 0 ? (
                  filteredClients.map((client) => (
                    <label
                      key={`client-${client.email}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#1A1A1A] cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={!!selectedRecipients.find(r => r.email === client.email)}
                        onCheckedChange={() => handleToggleRecipient(client)}
                        className="border-gray-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{client.name}</div>
                        <div className="text-xs text-gray-500 truncate">{client.email}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        client.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
                      }`}>
                        {client.status}
                      </span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No clients found</p>
                )}
              </div>
            </div>

            {/* Team Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Team Members ({filteredTeam.length})
                </h3>
                <button
                  onClick={() => handleSelectAll("team")}
                  className="text-xs text-orange-500 hover:text-orange-400"
                  data-testid="select-all-team"
                >
                  {filteredTeam.every(t => selectedRecipients.find(r => r.email === t.email)) 
                    ? "Deselect All" 
                    : "Select All"}
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                {filteredTeam.length > 0 ? (
                  filteredTeam.map((member) => (
                    <label
                      key={`team-${member.user_id || member.email}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#1A1A1A] cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={!!selectedRecipients.find(r => r.email === member.email)}
                        onCheckedChange={() => handleToggleRecipient(member)}
                        className="border-gray-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{member.name}</div>
                        <div className="text-xs text-gray-500 truncate">{member.email}</div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 capitalize">
                        {member.role?.replace("_", " ")}
                      </span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No team members found</p>
                )}
              </div>
            </div>

            {/* Add Custom Email */}
            <div className="mt-4 pt-4 border-t border-[#262626]">
              <button
                onClick={() => {
                  const email = prompt("Enter custom email address:");
                  const name = email ? prompt("Enter recipient name:") : null;
                  if (email && name) {
                    handleToggleRecipient({ email, name, type: "custom" });
                  }
                }}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-orange-500 transition-colors"
                data-testid="add-custom-recipient"
              >
                <Plus className="w-4 h-4" />
                Add custom email address
              </button>
            </div>
          </div>
        </div>
      ) : activeTab === "ai-update" ? (
        /* AI Updates Tab */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="ai-update-section">
          {/* AI Update Form */}
          <div className="card">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-500" />
              AI-Enhanced Client Update
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Write a short update and let AI transform it into a warm, professional message
            </p>

            <div className="space-y-4">
              {/* Short Update Input */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Your Quick Update *</label>
                <textarea
                  value={aiUpdate.shortUpdate}
                  onChange={(e) => {
                    setAiUpdate({...aiUpdate, shortUpdate: e.target.value});
                    if (aiPreview) setAiPreview(null);
                  }}
                  placeholder="e.g., meeting with contractor done, drawings approved, site work started..."
                  className="w-full min-h-[100px] p-3 bg-[#1A1A1A] border border-[#262626] rounded-lg text-white placeholder:text-gray-500 focus:border-pink-500 focus:outline-none resize-none"
                  data-testid="ai-update-input"
                />
              </div>

              {/* Project Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Link to Project (Optional)</label>
                <Select 
                  value={aiUpdate.projectId || "none"} 
                  onValueChange={(v) => {
                    setAiUpdate({...aiUpdate, projectId: v === "none" ? "" : v});
                    if (aiPreview) setAiPreview(null);
                  }}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-[#262626]">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tone Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Message Tone</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "warm_professional", label: "Warm & Professional", icon: "💼" },
                    { value: "formal", label: "Formal", icon: "📋" },
                    { value: "casual", label: "Friendly", icon: "👋" }
                  ].map((tone) => (
                    <button
                      key={tone.value}
                      onClick={() => {
                        setAiUpdate({...aiUpdate, tone: tone.value});
                        if (aiPreview) setAiPreview(null);
                      }}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        aiUpdate.tone === tone.value 
                          ? "border-pink-500 bg-pink-500/10" 
                          : "border-[#262626] hover:border-[#404040]"
                      }`}
                    >
                      <span className="text-xl">{tone.icon}</span>
                      <p className={`text-xs mt-1 ${aiUpdate.tone === tone.value ? "text-white" : "text-gray-400"}`}>
                        {tone.label}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleAiPreview}
                  disabled={aiPreviewing || !aiUpdate.shortUpdate || aiUpdate.selectedClients.length === 0}
                  variant="outline"
                  className="flex-1 border-pink-500/50 text-pink-400 hover:bg-pink-500/10"
                >
                  {aiPreviewing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  Preview with AI
                </Button>
                <Button
                  onClick={handleAiSend}
                  disabled={aiSending || !aiPreview}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600"
                >
                  {aiSending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send to {aiUpdate.selectedClients.length} Client(s)
                </Button>
              </div>
            </div>

            {/* AI Preview */}
            {aiPreview && (
              <div className="mt-6 p-4 bg-gradient-to-r from-pink-500/5 to-orange-500/5 border border-pink-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 className="w-4 h-4 text-pink-400" />
                  <span className="text-sm font-medium text-pink-400">AI Enhanced Preview</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">Subject: {aiPreview.subject}</p>
                <div className="bg-[#0f0f0f] p-4 rounded-lg">
                  <p className="text-white text-sm whitespace-pre-wrap leading-relaxed">
                    {aiPreview.enhanced_message}
                  </p>
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  Original: &quot;{aiPreview.original_update}&quot;
                </p>
              </div>
            )}
          </div>

          {/* Client Selection for AI Update */}
          <div className="card">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Select Clients ({aiUpdate.selectedClients.length} selected)
            </h2>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-[#1A1A1A] border-[#262626]"
              />
            </div>

            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2">
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <label
                    key={client.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      aiUpdate.selectedClients.includes(client.id) 
                        ? "bg-orange-500/10 border border-orange-500/30" 
                        : "hover:bg-[#1A1A1A] border border-transparent"
                    }`}
                  >
                    <Checkbox
                      checked={aiUpdate.selectedClients.includes(client.id)}
                      onCheckedChange={() => toggleAiClient(client.id)}
                      className="border-gray-600"
                    />
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">
                        {client.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{client.name}</div>
                      <div className="text-xs text-gray-500 truncate">{client.email}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                      client.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
                    }`}>
                      {client.status}
                    </span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">No clients found</p>
              )}
            </div>

            {filteredClients.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#262626] flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAiUpdate({...aiUpdate, selectedClients: filteredClients.map(c => c.id)})}
                  className="text-xs border-[#262626]"
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAiUpdate({...aiUpdate, selectedClients: []})}
                  className="text-xs border-[#262626]"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Campaign History */
        <div className="card" data-testid="campaign-history">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-orange-500" />
            Campaign History
          </h2>

          {campaigns.length > 0 ? (
            <div className="space-y-3">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.campaign_id}
                  className="p-4 bg-[#1A1A1A] rounded-lg border border-[#262626] hover:border-[#333] transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{campaign.subject}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {campaign.total_recipients} recipients
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-green-400" />
                          {campaign.sent_count} sent
                        </span>
                        {campaign.failed_count > 0 && (
                          <span className="flex items-center gap-1 text-red-400">
                            <XCircle className="w-3 h-3" />
                            {campaign.failed_count} failed
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {new Date(campaign.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(campaign.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No campaigns sent yet</p>
              <Button
                onClick={() => setActiveTab("compose")}
                className="mt-4 bg-orange-500 hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Campaign
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Campaigns;
